import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ===== helpers do ułamków ===== */

function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const r = a % b;
    a = b;
    b = r;
  }
  return a;
}

function simplifyFrac(frac) {
  let { num, den } = frac;
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function fracToLatex(frac) {
  const { num, den } = simplifyFrac(frac);
  if (den === 1) return `${num}`;
  const sign = num * den < 0 ? "-" : "";
  const absNum = Math.abs(num);
  const absDen = Math.abs(den);
  return `${sign}\\dfrac{${absNum}}{${absDen}}`;
}

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

/* ===== GENERATOR =====
   Dane: a1 całkowite (większy zakres),
   q dodatnie – całkowite lub ułamkowe (mianownik 2,3,4),
   n z zakresu 2..5, a_n (x) całkowite.
*/

export function generateTask() {
  // exponent = n - 1
  const exponent = randomInt(1, 4); // 1..4
  const n = exponent + 1;

  // losujemy q = qNum / qDen
  const qDen = Math.random() < 0.5 ? pick([2, 3, 4]) : 1;
  let qNum;
  do {
    qNum = randomInt(2, 8);
  } while (qDen > 1 && (qNum <= qDen || gcd(qNum, qDen) !== 1));

  const qValue = qNum / qDen;

  // a1 = s * k * qDen^{exponent}, żeby x wyszło całkowite
  const sign = Math.random() < 0.5 ? -1 : 1;
  const k = randomInt(1, 6);
  const base = Math.pow(qDen, exponent);
  const a1 = sign * k * base;

  // x = a1 * q^{exponent} = sign * k * qNum^{exponent}
  const x = sign * k * Math.pow(qNum, exponent);

  // ułamek (x / a1) = (qNum^{exp}) / (qDen^{exp})
  const quotientFrac = simplifyFrac({
    num: Math.pow(qNum, exponent),
    den: Math.pow(qDen, exponent)
  });

  const qFrac = simplifyFrac({ num: qNum, den: qDen });

  return {
    type: "FindTermIndexGeoTask",
    data: {
      a1,
      q: qValue,
      n,
      x,
      exponent,
      qFrac,
      quotientFrac
    }
  };
}

/* ===== RENDER ===== */

export function renderTask({ data }) {
  const { a1, qFrac, x } = data;

  const qLatex = fracToLatex(qFrac);

  return (
    <>
      <p>
        Dany jest ciąg geometryczny, w którym{" "}
        <InlineMath math={`a_1 = ${a1}`} /> oraz{" "}
        <InlineMath math={`q = ${qLatex}`} />.
      </p>
      <p>
        Wiadomo, że jednym z jego wyrazów jest liczba{" "}
        <InlineMath math={`${x}`} />.
      </p>
      <p>
        Podaj numer wyrazu <InlineMath math="n" />, dla którego{" "}
        <InlineMath math={`a_n = ${x}`} />.
      </p>
    </>
  );
}

/* ===== INPUT ===== */

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-2">
      <InlineMath math="n =" />
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label="n"
      />
    </div>
  );
}

/* ===== CHECK =====
   Korzystamy z userAnswer.parsed (FractionInput),
   akceptujemy np. "4", "4.0", ale nie 4.5.
*/

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const given = userAnswer.parsed;
  if (given === null || isNaN(given)) return false;

  // musi być praktycznie równe całkowitemu n
  return Math.abs(given - data.n) < 0.01;
}

/* ===== ODPOWIEDŹ ===== */

export function renderAnswer({ data }) {
  return <InlineMath math={`n = ${data.n}`} />;
}

/* ===== WYJAŚNIENIE ===== */

export function renderExplanation({ data }) {
  const { a1, qFrac, n, x, quotientFrac, exponent } = data;

  const qLatex = fracToLatex(qFrac);
  const quotientLatex = fracToLatex(quotientFrac);

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1:</strong> Korzystamy ze wzoru ogólnego ciągu
        geometrycznego.
      </p>
      <BlockMath math={`a_n = a_1 \\cdot q^{n-1}`} />

      <p>
        <strong>Krok 2:</strong> Podstawiamy dane do wzoru.
      </p>
      <BlockMath math={`${x} = ${a1} \\cdot \\left(${qLatex}\\right)^{n-1}`} />
      <BlockMath math={`\\frac{x}{a_1} = q^{n-1}`} />
      <BlockMath
        math={`\\frac{${x}}{${a1}} = ${quotientLatex}`}
      />

      <p>
        <strong>Krok 3:</strong> Zauważamy, że{" "}
        <InlineMath math={quotientLatex} /> można zapisać jako potęgę ilorazu{" "}
        <InlineMath math="q" />.
      </p>
      <BlockMath
        math={`${quotientLatex} = \\left(${qLatex}\\right)^{${exponent}}`}
      />
      <p>
        Mamy więc równość potęg o jednakowych podstawach, więc porównujemy
        wykładniki.
      </p>
      <BlockMath
        math={`q^{n-1} = q^{${exponent}} \\Rightarrow n-1 = ${exponent}`}
      />
      <BlockMath math={`n = ${n}`} />
    </div>
  );
}

/* ===== ODP. UŻYTKOWNIKA ===== */

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={`n = ${userAnswer.raw}`} />;
}
